<template>
    <div class="rating-chart">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('message.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('message.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('message.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                </div>
            </div>
            <div class="right"></div>
        </div>
        <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        <StackedAreaChart
            :title="$t('report.ratingTimeSeries')"
            :x-axis-data="getXAxisData()"
            :legends="legends"
            :colors="colors"
            :series="getSeries()"
            v-else
        />
        <div class="stats">
            <StatCard
                :title="$t('report.totalRatingCount')"
                :value="stats.totalRatingCount"
                :unit="$t('report.rate')"
            />
            <StatCard :title="$t('report.averageRating')" :value="stats.AverageRating" :unit="$t('message.point')" />
            <StatCard
                :title="$t('report.weightedAverageRating')"
                :value="stats.WeightedAverageRating"
                :unit="$t('message.point')"
            />
        </div>
        <div class="stats">
            <StatCard
                :key="key"
                :title="key"
                :value="stats.ratingCounts[key]"
                v-for="key of Object.keys(stats.ratingCounts)"
            />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import StackedAreaChart from "./StackedAreaChart.vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import StatCard from "./StatCard.vue";
import IconButton from "../../../../Settings/components/IconButton.vue";
import TableView from "./TableView.vue";
import { expertToExcel } from "../../../../../../utils/utils";

export default Vue.extend({
    name: "RatingChart",
    components: { TableView, IconButton, StatCard, StackedAreaChart },
    props: {
        /**
         * [
         *     {
         *         "date": "2023-09-25T00:00:00+00:00",
         *         "items": [
         *             {
         *                 "createdDate": "2023-09-25T22:22:27.765Z",
         *                 "duration": 1202,
         *                 "successMatchCount": 0,
         *                 "failedMatchCount": 0
         *             },
         *         ]
         *     }
         * ]
         */
        data: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            colors: ["#80FFA5", "#00DDFF", "#37A2FF", "#FF0087", "#FFBF00", "#ec3131"],
            legends: [this.$t("report.notReport"), "⭐", "⭐⭐", "⭐⭐⭐", "⭐⭐⭐⭐", "⭐⭐⭐⭐⭐"],
            stats: this.getStats(),
            tableMode: false,
        };
    },
    methods: {
        getCols() {
            return [
                {
                    title: this.$t("message.date"),
                    dataIndex: "date",
                    key: "date",
                },
                ...["Not Rated", "⭐", "⭐⭐", "⭐⭐⭐", "⭐⭐⭐⭐", "⭐⭐⭐⭐⭐"].map((legend) => ({
                    title: legend === "Not Rated" ? this.$t("report.notRated") : legend,
                    dataIndex: legend,
                    key: legend,
                })),
            ];
        },
        getRows() {
            const { buckets, timeSeriesType } = this.data;
            const rows = buckets.map((bucket) => {
                const rates = bucket.items.reduce(
                    (acc, item) => {
                        if (!item.point) {
                            acc["Not Rated"] += 1;
                        } else if (item.point === 1) {
                            acc["⭐"] += 1;
                        } else if (item.point === 2) {
                            acc["⭐⭐"] += 1;
                        } else if (item.point === 3) {
                            acc["⭐⭐⭐"] += 1;
                        } else if (item.point === 4) {
                            acc["⭐⭐⭐⭐"] += 1;
                        } else if (item.point === 5) {
                            acc["⭐⭐⭐⭐⭐"] += 1;
                        }

                        return acc;
                    },
                    {
                        "Not Rated": 0,
                        "⭐": 0,
                        "⭐⭐": 0,
                        "⭐⭐⭐": 0,
                        "⭐⭐⭐⭐": 0,
                        "⭐⭐⭐⭐⭐": 0,
                    }
                );

                const date = {
                    hourly: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    daily: moment(bucket.date).format("DD/MM/YYYY"),
                    monthly: moment(bucket.date).format("MM/YYYY"),
                }[timeSeriesType];
                return {
                    date,
                    ...rates,
                };
            });
            return rows;
        },
        getStats() {
            const { buckets } = this.data;
            const items = _.flatMap(buckets, (bucket) => bucket.items).map((x) => x.point);
            const notEmptyItems = items.filter(Boolean);
            const totalRatingCount = notEmptyItems.length;
            const AverageRating = _.mean(notEmptyItems).toFixed(2);
            const WeightedAverageRating = AverageRating;
            const ratingCounts = items.reduce(
                (acc, point) => {
                    if (!point) {
                        acc["Not Rated"] += 1;
                    } else if (point === 1) {
                        acc["⭐"] += 1;
                    } else if (point === 2) {
                        acc["⭐⭐"] += 1;
                    } else if (point === 3) {
                        acc["⭐⭐⭐"] += 1;
                    } else if (point === 4) {
                        acc["⭐⭐⭐⭐"] += 1;
                    } else if (point === 5) {
                        acc["⭐⭐⭐⭐⭐"] += 1;
                    }
                    return acc;
                },
                {
                    "Not Rated": 0,
                    "⭐": 0,
                    "⭐⭐": 0,
                    "⭐⭐⭐": 0,
                    "⭐⭐⭐⭐": 0,
                    "⭐⭐⭐⭐⭐": 0,
                }
            );

            return {
                totalRatingCount,
                AverageRating,
                WeightedAverageRating,
                ratingCounts,
            };
        },
        getXAxisData() {
            const { buckets, timeSeriesType } = this.data;

            return buckets.map((item) => {
                if (timeSeriesType === "daily") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hourly") {
                    return moment(item.date).format("HH:mm");
                } else {
                    //monthly
                    return moment(item.date).format("MMM");
                }
            });
        },
        getSeries() {
            const { buckets, timeSeriesType } = this.data;

            const gradients = {
                "Not Rated": {
                    color1: "rgb(128, 255, 165)",
                    color2: "rgb(1, 191, 236)",
                },
                "⭐": {
                    color1: "rgb(0, 221, 255)",
                    color2: "rgb(77, 119, 255)",
                },
                "⭐⭐": {
                    color1: "rgb(55, 162, 255)",
                    color2: "rgb(116, 21, 219)",
                },
                "⭐⭐⭐": {
                    color1: "rgb(255, 0, 135)",
                    color2: "rgb(135, 0, 157)",
                },
                "⭐⭐⭐⭐": {
                    color1: "rgb(255, 191, 0)",
                    color2: "rgb(224, 62, 76)",
                },
                "⭐⭐⭐⭐⭐": {
                    color1: "rgb(236, 49, 49)",
                    color2: "rgb(255, 0, 0)",
                },
            };
            const ratingMap = {
                "Not Rated": undefined,
                "⭐": 1,
                "⭐⭐": 2,
                "⭐⭐⭐": 3,
                "⭐⭐⭐⭐": 4,
                "⭐⭐⭐⭐⭐": 5,
            };
            const series = this.legends.map((legend, i) => {
                const data = buckets.map((bucket) => {
                    const value = _.chain(bucket.items)
                        .filter((item) => {
                            return item.point === ratingMap[legend];
                        })
                        .map((x) => 1)
                        .sum()
                        .value();
                    return value || 0;
                });
                const seriesData = {
                    name: legend,
                    type: "line",
                    stack: "Total",
                    smooth: true,
                    lineStyle: {
                        width: 0,
                    },
                    showSymbol: false,
                    areaStyle: {
                        opacity: 0.8,
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            {
                                offset: 0,
                                color: gradients[legend].color1,
                            },
                            {
                                offset: 1,
                                color: gradients[legend].color2,
                            },
                        ]),
                    },
                    emphasis: {
                        focus: "series",
                    },
                    data,
                };
                return seriesData;
            });
            return series;
        },
        exportToExcel() {
            const { buckets, timeSeriesType } = this.data;
            const data = buckets.map((bucket) => {
                const rates = bucket.items.reduce(
                    (acc, item) => {
                        if (!item.point) {
                            acc["Not Rated"] += 1;
                        } else if (item.point === 1) {
                            acc["⭐"] += 1;
                        } else if (item.point === 2) {
                            acc["⭐⭐"] += 1;
                        } else if (item.point === 3) {
                            acc["⭐⭐��"] += 1;
                        } else if (item.point === 4) {
                            acc["⭐⭐⭐⭐"] += 1;
                        } else if (item.point === 5) {
                            acc["⭐⭐⭐⭐⭐"] += 1;
                        }
                        return acc;
                    },
                    {
                        "Not Rated": 0,
                        "⭐": 0,
                        "⭐⭐": 0,
                        "⭐⭐⭐": 0,
                        "⭐⭐⭐⭐": 0,
                        "⭐⭐⭐⭐⭐": 0,
                    }
                );

                const date = {
                    hourly: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    daily: moment(bucket.date).format("DD/MM/YYYY"),
                    monthly: moment(bucket.date).format("MM/YYYY"),
                }[timeSeriesType];

                return {
                    date,
                    ...rates,
                };
            });
            const stats = this.getStats();
            const excelData = [
                ...data,
                {
                    date: "Toplam Derecelendirme Sayısı",
                    ...stats.ratingCounts,
                },
                {
                    date: "Ortalama puan��",
                    "Average Rating": stats.AverageRating,
                },
                {
                    date: "Ağırlıklı Ortalama Not",
                    "Weighted Average Rating": stats.WeightedAverageRating,
                },
            ];
            expertToExcel(excelData, `rating_timeseries.xlsx`, "Sheet 1");
        },
    },
    watch: {
        data: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
            deep: true,
        },
    },
});
</script>

<style scoped lang="scss">
.rating-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
